
import React, { useState, useMemo } from 'react';
import Markdown from 'react-markdown';
import { MeetingNote } from '../types';

interface Props {
  notes: MeetingNote[];
  onRemove: (id: string) => void;
  onSummarize: () => void;
  isProcessing: boolean;
}

type FilterType = 'all' | 'gmail' | 'drive' | 'manual' | 'chat';

const MeetingList: React.FC<Props> = ({ notes, onRemove, onSummarize, isProcessing }) => {
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'gmail': return '📧';
      case 'gdoc': return '📄';
      case 'gsheet': return '📊';
      case 'chat': return '💬';
      default: return '✍️';
    }
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'gmail': return 'Gmail Note';
      case 'gdoc': return 'Google Doc';
      case 'gsheet': return 'Google Sheet';
      case 'chat': return 'Google Chat';
      default: return 'Manual Entry';
    }
  };

  const filteredNotes = useMemo(() => {
    if (activeFilter === 'all') return notes;
    return notes.filter(note => {
      if (activeFilter === 'gmail') return note.source === 'gmail';
      if (activeFilter === 'manual') return note.source === 'manual';
      if (activeFilter === 'drive') return note.source === 'gdoc' || note.source === 'gsheet';
      if (activeFilter === 'chat') return note.source === 'chat';
      return true;
    });
  }, [notes, activeFilter]);

  const filters: { id: FilterType; label: string; count: number }[] = [
    { id: 'all', label: 'All', count: notes.length },
    { id: 'gmail', label: 'Gmail', count: notes.filter(n => n.source === 'gmail').length },
    { id: 'drive', label: 'Drive', count: notes.filter(n => n.source === 'gdoc' || n.source === 'gsheet').length },
    { id: 'chat', label: 'Chat', count: notes.filter(n => n.source === 'chat').length },
    { id: 'manual', label: 'Manual', count: notes.filter(n => n.source === 'manual').length },
  ];

  return (
    <div className="space-y-4">
      {/* Primary Action Section - Always visible at the top */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-2">
          <h3 className="font-bold text-slate-800 uppercase text-[10px] tracking-[0.2em]">Current Workspace</h3>
          <span className="bg-indigo-100 text-indigo-700 text-[10px] font-bold px-2 py-0.5 rounded-full">{notes.length} Items</span>
        </div>
        
        <button 
          onClick={onSummarize}
          disabled={isProcessing || notes.length === 0}
          className={`w-full flex items-center justify-center py-4 px-6 rounded-2xl font-bold text-base shadow-xl transition-all transform active:scale-95 ${
            isProcessing || notes.length === 0
              ? 'bg-slate-200 cursor-not-allowed text-slate-400 shadow-none' 
              : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-200/50 hover:shadow-indigo-300/50'
          }`}
        >
          {isProcessing ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Synthesizing Context...
            </>
          ) : (
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>Summarize Workspace</span>
            </div>
          )}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-1.5 px-1 py-1">
        {filters.map((filter) => (
          <button
            key={filter.id}
            onClick={() => setActiveFilter(filter.id)}
            className={`px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-tight transition-all border ${
              activeFilter === filter.id
                ? 'bg-slate-900 text-white border-slate-900 shadow-md'
                : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
            }`}
          >
            {filter.label} <span className={activeFilter === filter.id ? 'opacity-50' : 'text-slate-400'}>{filter.count}</span>
          </button>
        ))}
      </div>

      {/* List Container */}
      <div className="relative">
        {filteredNotes.length === 0 ? (
          <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center">
            <p className="text-slate-400 text-xs font-medium">
              {notes.length === 0 ? 'No workspace data found.' : 'No items match your filter.'}
            </p>
          </div>
        ) : (
          <div className="max-h-[450px] overflow-y-auto space-y-3 pr-2 custom-scrollbar pb-4">
            {filteredNotes.map((note) => (
              <div 
                key={note.id} 
                className={`group bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:border-indigo-300 transition-all animate-in fade-in zoom-in-95 duration-200 hover:shadow-md ${note.url ? 'cursor-pointer' : ''}`}
                onClick={() => {
                  if (note.url) window.open(note.url, '_blank');
                }}
              >
                <div className="flex justify-between items-start mb-1.5">
                  <h4 className="font-bold text-slate-900 truncate pr-4 text-[13px] flex items-center">
                    <span className="mr-2 filter grayscale group-hover:grayscale-0 transition-all">{getSourceIcon(note.source)}</span>
                    {note.title}
                  </h4>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(note.id);
                    }}
                    className="text-slate-300 hover:text-red-500 transition-colors p-1 -m-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="flex items-center justify-between text-[9px] mb-2.5">
                  <div className="text-slate-400 font-bold">{note.date}</div>
                  <span className="bg-slate-50 text-slate-500 px-1.5 py-0.5 rounded font-bold uppercase tracking-tighter border border-slate-100">
                    {getSourceLabel(note.source)}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 line-clamp-2 leading-relaxed">
                  <Markdown
                    components={{
                      a: ({node, ...props}) => <a {...props} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline font-medium" onClick={(e) => e.stopPropagation()} />,
                      p: ({node, ...props}) => <span {...props} className="mr-1" />
                    }}
                  >
                    {note.content}
                  </Markdown>
                </div>
              </div>
            ))}
            {/* Fade effect at the bottom of the list */}
            <div className="sticky bottom-0 h-8 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingList;
